// src/slices/campaignSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCampaigns,
  updateCampaignStatus,
  updateCampaign,
  campaignService,
} from "../company/services/companyAuthService";
import axios from "axios";

// Async Thunks
export const fetchCampaigns = createAsyncThunk(
  "campaign/fetchCampaigns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCampaigns();
      return response.campaigns;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCampaignStatusThunk = createAsyncThunk(
  "campaign/updateStatus",
  async ({ id, status, isDeactivated }, { rejectWithValue }) => {
    try {
      const response = await updateCampaignStatus(id, {
        status,
        isDeactivated
      });
      
      return response.campaign;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCampaignThunk = createAsyncThunk(
  "campaign/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateCampaign(id, data);
      return response.campaign;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const associateAffiliateWithCampaign = createAsyncThunk(
  "campaign/associateAffiliate",
  async ({ affiliateId, campaignId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/company/campaigns/${campaignId}/affiliates`, {
        affiliateId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to associate affiliate with campaign"
      );
    }
  }
);

export const removeAffiliateFromCampaign = createAsyncThunk(
  "campaign/removeAffiliate",
  async ({ affiliateId, campaignId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/company/campaigns/${campaignId}/affiliates/${affiliateId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove affiliate from campaign"
      );
    }
  }
);

export const fetchAffiliateCampaigns = createAsyncThunk(
  "campaign/fetchAffiliateCampaigns",
  async (affiliateId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/company/affiliates/${affiliateId}/campaigns`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch affiliate campaigns"
      );
    }
  }
);

export const getCampaignById = createAsyncThunk(
  "campaign/getCampaignById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await campaignService.getCampaignById(id);
      return response.campaign;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCampaignAffiliatesThunk = createAsyncThunk(
  "campaign/fetchAffiliates",
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await campaignService.getCampaignAffiliates(campaignId);
      return response.affiliates;
    } catch (error) {
      return rejectWithValue(error.message || "Error fetching affiliates");
    }
  }
);



// Initial State
const initialState = {
  campaignData: {
    general: null,
    commission: null,
    landingPage: null,
  },
  loading: false,
  error: null,
  campaigns: [],
  currentCampaign: null,
  lastFetched: null,
  affiliateCampaigns: [],
  selectedCampaign: null,
  campaignAffiliates: [],
  affiliatesLoading: false,
  affiliatesError: null,
  selectedAffiliates: [],
};

// Slice
const campaignSlice = createSlice({
  name: "campaign",
  initialState,
  reducers: {
    setGeneralData: (state, action) => {
      state.campaignData.general = action.payload;
    },
    setCommissionData: (state, action) => {
      state.campaignData.commission = action.payload;
    },
    setLandingPageData: (state, action) => {
      state.campaignData.landingPage = action.payload;
    },
    setCampaigns: (state, action) => {
      state.campaigns = action.payload;
    },
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload;
    },
    clearCampaignData: (state) => {
      state.campaigns = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearSelectedCampaign: (state) => {
      state.selectedCampaign = null;
    },
    setCampaignAffiliates: (state, action) => {
      state.campaignAffiliates = action.payload;
    },
    setAffiliatesLoading: (state, action) => {
      state.affiliatesLoading = action.payload;
    },
    setAffiliatesError: (state, action) => {
      state.affiliatesError = action.payload;
    },
    clearAffiliates: (state) => {
      state.campaignAffiliates = [];
      state.affiliatesLoading = false;
      state.affiliatesError = null;
    },
    setSelectedAffiliates: (state, action) => {
      state.selectedAffiliates = action.payload;
    },
    updateSelectedCampaign: (state, action) => {
      if (state.selectedCampaign) {
        state.selectedCampaign = {
          ...state.selectedCampaign,
          ...action.payload
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.campaigns = Array.isArray(action.payload) ? action.payload : [];
        state.loading = false;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.campaigns = [];
      })

      .addCase(updateCampaignStatusThunk.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.payload._id);
        if (index !== -1) state.campaigns[index] = action.payload;
      })

      .addCase(updateCampaignThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaignThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCampaign = action.payload;
        const index = state.campaigns.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(updateCampaignThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(associateAffiliateWithCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(associateAffiliateWithCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.affiliateCampaigns.push(action.payload);
      })
      .addCase(associateAffiliateWithCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeAffiliateFromCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAffiliateFromCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.affiliateCampaigns = state.affiliateCampaigns.filter(
          (campaign) => campaign._id !== action.payload._id
        );
      })
      .addCase(removeAffiliateFromCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAffiliateCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAffiliateCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.affiliateCampaigns = action.payload;
      })
      .addCase(fetchAffiliateCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCampaignById.fulfilled, (state, action) => {
        state.selectedCampaign = action.payload;
        state.loading = false;
      })
      .addCase(getCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCampaignAffiliatesThunk.pending, (state) => {
        state.affiliatesLoading = true;
        state.affiliatesError = null;
      })
      .addCase(fetchCampaignAffiliatesThunk.fulfilled, (state, action) => {
        state.affiliatesLoading = false;
        state.campaignAffiliates = action.payload;
      })
      .addCase(fetchCampaignAffiliatesThunk.rejected, (state, action) => {
        state.affiliatesLoading = false;
        state.affiliatesError = action.payload;
      });
  },
});

export const {
  setGeneralData,
  setCommissionData,
  setLandingPageData,
  setCampaigns,
  setCurrentCampaign,
  clearCampaignData,
  setLoading,
  setError,
  clearSelectedCampaign,
  setCampaignAffiliates,
  setAffiliatesLoading,
  setAffiliatesError,
  clearAffiliates,
  setSelectedAffiliates,
  updateSelectedCampaign,
} = campaignSlice.actions;

export default campaignSlice.reducer;



